import { menus } from "@/constants/menu";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useUnreadContactCount } from "@/hooks/useUnreadContactCount";
import { useMatchListSessionStorage } from "@/hooks/useMatchListSessionStorage";
import { useInformationListSessionStorage } from "@/hooks/useInformationListSessionStorage";
import { useContactListSessionStorage } from "@/hooks/useContactListSessionStorage";
import { background, black, state, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { TMenu } from "@/types/menu";
import { hexToRgba } from "@/utils/pieces";
import { Divider, Drawer, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useMemo } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes, MatchRoutes } from "@/routes/config";

type TAppDrawerProps = { isOpenDrawer: boolean; toggleDrawer: () => void };

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const { unreadCount } = useUnreadContactCount();
  const hasUnreadContact = unreadCount > 0;

  const DrawerContent = useMemo(
    () => (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        <Stack gap={1.5}>
          {menus.map((item, index) => {
            if (index === 0)
              return (
                <Stack gap={2} key={index}>
                  <MenuItem key={item.text} {...item} hasUnreadContact={hasUnreadContact} />
                  <Divider color={background.white} sx={{ opacity: 0.3 }} key={index} />
                </Stack>
              );
            return <MenuItem key={item.text} {...item} hasUnreadContact={hasUnreadContact} />;
          })}
        </Stack>
      </ListWrapper>
    ),
    [pathname, isLaptop, hasUnreadContact],
  );

  if (!isLaptop) return DrawerContent;
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 57px)", top: "64px" } }}
    >
      {/* <IconCloseWrapper>
        <IconCloseBtn onClick={toggleDrawer} />
      </IconCloseWrapper> */}
      {DrawerContent}
    </Drawer>
  );
};

type TMenuItemProps = TMenu & {
  hasUnreadContact?: boolean;
};

const MenuItem: FC<TMenuItemProps> = (props) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBackUrl: getMatchBackUrl } = useMatchListSessionStorage();
  const { getBackUrl: getInformationBackUrl } = useInformationListSessionStorage();
  const { getBackUrl: getContactBackUrl } = useContactListSessionStorage();
  const { hasUnreadContact = false } = props;

  const isActive = props.relatedRoutes?.some((route) => Boolean(matchRoutes([{ path: route }], pathname)));

  const childrenContent = useMemo(() => {
    return props.children?.map((item) => <MenuItem key={item.text} {...item} hasUnreadContact={hasUnreadContact} />);
  }, [props.children, hasUnreadContact]);

  const content = useMemo(() => {
    const Icon = props.icon;
    const isContactMenu = props.path?.includes("/contact/");
    const hasUnreadIndicator = isContactMenu && hasUnreadContact;

    return (
      <Stack>
        <MenuItemContent
          p={isLaptop && !Icon ? 0 : 1}
          pl={isLaptop ? 1 : props.path ? 2 : 1}
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent={isLaptop ? "center" : "inherit"}
        >
          {Icon && (
            <IconWrapper>
              <Icon fontSize={24} color={text.white} />
              {hasUnreadIndicator && (
                <UnreadIndicator>
                  <svg width="15" height="15" viewBox="0 0 15 15">
                    <circle cx="7.5" cy="7.5" r="7.5" fill={state.error_1} />
                  </svg>
                </UnreadIndicator>
              )}
            </IconWrapper>
          )}

          {!isLaptop && (
            <Typography variant={props.path ? "body14Bold" : "cap12Bold"} color={props.path ? text.white : text.tertiary}>
              {props.text}
            </Typography>
          )}
        </MenuItemContent>
        <Stack gap={1}>{childrenContent}</Stack>
      </Stack>
    );
  }, [props.text, props.icon, props.path, isLaptop, hasUnreadContact, navigate]);

  if (!props.path) return content;
  if (isLaptop)
    return (
      <Tooltip title={props.text} placement="right">
        <ListItemWrapper
          isLaptop={isLaptop}
          className={isActive ? "active" : ""}
          onClick={() => {
            if (!props.path) return;

            if (MatchRoutes.includes(props.path)) {
              const backUrl = getMatchBackUrl();
              navigate(backUrl);
              return;
            }

            if (props.path === AppRoutes.information) {
              const backUrl = getInformationBackUrl();
              navigate(backUrl);
              return;
            }

            if (props.path === AppRoutes.unresolvedContact) {
              // Force remove cache and navigate to saved tab/page
              queryClient.removeQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST] });
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST], refetchType: "active" });
              const backUrl = getContactBackUrl();
              navigate(backUrl);
              return;
            }

            navigate(props.path);
          }}
        >
          {content}
        </ListItemWrapper>
      </Tooltip>
    );
  return (
    <ListItemWrapper
      isLaptop={isLaptop}
      className={isActive ? "active" : ""}
      onClick={() => {
        if (!props.path) return;

        if (MatchRoutes.includes(props.path)) {
          const backUrl = getMatchBackUrl();
          navigate(backUrl);
          return;
        }

        if (props.path === AppRoutes.information) {
          const backUrl = getInformationBackUrl();
          navigate(backUrl);
          return;
        }

        if (props.path === AppRoutes.unresolvedContact) {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACT.FETCH_LIST] });
          const backUrl = getContactBackUrl();
          navigate(backUrl);
          return;
        }

        navigate(props.path);
      }}
    >
      {content}
    </ListItemWrapper>
  );
};

const ListWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${black[30]};
  color: ${text.white};
  min-height: calc(100vh - 55px);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 55px;
  z-index: ${zIndex.drawer};
`;

const ListItemWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;

const MenuItemContent = styled(Stack)`
  position: relative;
`;

const IconWrapper = styled(Box)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const UnreadIndicator = styled(Box)`
  position: absolute;
  top: -6px;
  right: -7px;
  pointer-events: none;
  z-index: 1;
`;
